import React, { useRef, useState } from "react";
import BaseModal from "../../../components/ui/BaseModal";
import BaseButton from "../../../components/ui/BaseButton";
import BaseAutoSelect from "../../../components/ui/BaseAutoSelect";
import BaseTextField from "../../../components/ui/BaseTextField";
import BaseCard from "../../../components/ui/BaseCard";
import { useSelector, useDispatch } from "react-redux";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  updateWorkOrderCompanyExpenses,
  uploadWorkOrderExpenseAttachments,
} from "../../../services/api/workOrder";
import { showToast } from "../../../store/toast/toastSlice";
import { CurrencySymbol } from "../../../utils/enum";
import { isEqual } from "lodash";

// What Save would actually send, so the button only lights up when pressing it would
// change something. Rows without a type are dropped here exactly as they are on save,
// and an attachment counts by its file name — a newly picked file has no server url yet.
const buildExpensesSnapshot = (currency, expenses) => ({
  currency: currency || "",
  expenses: (expenses || [])
    .filter((expense) => expense.expenseType)
    .map((expense) => ({
      id: expense.id,
      expenseType: expense.expenseType,
      description: expense.description || "",
      amount: parseFloat(expense.amount) || 0,
      attachment: expense.attachment?.fileName || null,
    })),
});

const EXPENSE_OPTIONS = [
  { label: "Travel Expense", value: "TRAVEL" },
  { label: "Tools Expense", value: "TOOLS" },
  { label: "Material Expense", value: "MATERIAL" },
  { label: "All Options", value: "ALL" },
  { label: "Other", value: "OTHER" },
];

const EditCompanyExpensesModal = ({
  open,
  close,
  companyExpenses: initialExpenses,
  currency: initialCurrency,
}) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const id = useSelector((state) => state.workOrder.workOrderDetails.id);

  const allCurrencies = Object.keys(CurrencySymbol);
  const [companyCurrency, setCompanyCurrency] = useState(initialCurrency || "");
  const [expenses, setExpenses] = useState(
    // Ensure every row has a stable key so file uploads can correlate back to the right row.
    (initialExpenses || []).map((e) => ({ ...e, expenseKey: e.expenseKey || crypto.randomUUID() })),
  );
  const fileInputRefs = useRef({});
  const [isSaving, setIsSaving] = useState(false);
  const baseline = useRef(buildExpensesSnapshot(initialCurrency, initialExpenses)).current;
  const hasChanges = !isEqual(buildExpensesSnapshot(companyCurrency, expenses), baseline);

  const { mutateAsync } = useMutation({
    mutationFn: updateWorkOrderCompanyExpenses,
  });

  const handleAddExpense = () => {
    setExpenses((prev) => [
      ...prev,
      {
        id: Date.now(),
        expenseType: "",
        description: "",
        amount: "",
        expenseKey: crypto.randomUUID(),
        attachment: null,
      },
    ]);
  };

  const handleChange = (index, field, value) => {
    setExpenses((prev) => prev.map((exp, i) => (i === index ? { ...exp, [field]: value } : exp)));
  };

  const handleFileChange = (index, e) => {
    const file = e.target.files[0];
    if (!file) return;
    handleChange(index, "attachment", {
      fileName: file.name,
      url: URL.createObjectURL(file),
      file,
    });
  };

  const handleRemove = (expenseId) => {
    setExpenses((prev) => prev.filter((exp) => exp.id !== expenseId));
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    try {
      const formatted = expenses
        .filter((exp) => exp.expenseType)
        .map(({ id: expId, expenseType, description, amount, expenseKey }) => ({
          id: expId,
          expenseType,
          description,
          amount: parseFloat(amount) || 0,
          expenseKey,
        }));

      await mutateAsync({
        id,
        companyCurrency: companyCurrency || null,
        companyExpenses: formatted,
      });

      const withFiles = expenses.filter((exp) => exp.expenseType && exp.attachment?.file);
      if (withFiles.length) {
        const formData = new FormData();
        formData.append(
          "expenses",
          JSON.stringify(withFiles.map((e) => ({ expenseKey: e.expenseKey }))),
        );
        withFiles.forEach((e) => {
          const fileWithKey = new File(
            [e.attachment.file],
            `${e.expenseKey}__-${e.attachment.file.name}`,
            {
              type: e.attachment.file.type,
            },
          );
          formData.append("files", fileWithKey);
        });
        await uploadWorkOrderExpenseAttachments({ workOrderId: id, formData });
      }

      await queryClient.invalidateQueries(["workOrderPayment"]);
      dispatch(
        showToast({ message: "Techdomain expenses updated successfully!", type: "success" }),
      );
      close();
    } catch {
      dispatch(showToast({ message: "Failed to update expenses.", type: "error" }));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <BaseModal open={open} close={close}>
      <div className="py-7 px-12 xl:w-[900px] lg:w-[700px] sm:w-[600px]">
        <h6 className="text-dark font-normal border-b pb-1">Edit Techdomain Expenses</h6>

        <BaseCard className="pt-4 pb-6 xl:px-16 px-5 mt-4 rounded-lg" elevation={1}>
          <div className="lg:col-span-1 col-span-2 mb-6">
            <p className="font-normal mb-1">Currency:</p>
            <BaseAutoSelect
              fullHeight={false}
              name="companyCurrency"
              onChange={(e) => setCompanyCurrency(e.target.value)}
              options={allCurrencies.map((c) => ({ label: c, value: c }))}
              value={companyCurrency}
            />
          </div>

          {expenses.map((expense, index) => (
            <div key={expense.id} className="bg-light rounded-lg pt-4 pb-8 lg:px-10 px-4 mt-4">
              <div className="flex justify-end">
                <i
                  className="fa-solid fa-trash-can cursor-pointer"
                  onClick={() => handleRemove(expense.id)}
                ></i>
              </div>
              <div className="grid grid-cols-2 gap-x-12 gap-y-4">
                <div className="lg:col-span-1 col-span-2">
                  <BaseAutoSelect
                    fullHeight={false}
                    label="Expense Type"
                    name="expenseType"
                    labelShrink={false}
                    required
                    isDefaultField={false}
                    onChange={(e) => handleChange(index, "expenseType", e.target.value)}
                    options={EXPENSE_OPTIONS}
                    defaultValue={expense.expenseType}
                    value={expense.expenseType}
                  />
                </div>
                <div className="lg:col-span-1 col-span-2">
                  <BaseTextField
                    fullHeight={false}
                    label="Description"
                    name="description"
                    labelShrink={false}
                    isDefaultField={false}
                    value={expense.description}
                    onChange={(e) => handleChange(index, "description", e.target.value)}
                  />
                </div>
                <div className="lg:col-span-1 col-span-2">
                  <BaseTextField
                    fullHeight={false}
                    label="Amount"
                    name="amount"
                    labelShrink={false}
                    required
                    type="float"
                    isDefaultField={false}
                    value={expense.amount}
                    onChange={(e) => handleChange(index, "amount", e.target.value)}
                  />
                </div>

                <div className="lg:col-span-1 col-span-2">
                  <p className="font-normal mb-2">
                    Attachment <span className="text-gray-400 text-sm">(optional)</span>
                  </p>

                  <input
                    type="file"
                    ref={(el) => (fileInputRefs.current[index] = el)}
                    onChange={(e) => handleFileChange(index, e)}
                    style={{ display: "none" }}
                  />

                  <div
                    style={{ boxShadow: "0px 0px 6px 0px rgba(0, 0, 0, 0.10)" }}
                    className="w-full flex h-[47px] justify-between items-center px-3 py-1 rounded-md"
                  >
                    <div className="flex-1 min-w-0 rounded-md flex items-center">
                      {expense.attachment ? (
                        <>
                          {expense.attachment.url ? (
                            <a
                              href={expense.attachment.url}
                              download={expense.attachment.fileName}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="py-1 cursor-pointer truncate min-w-0 text-blue-500 hover:underline font-300"
                            >
                              {expense.attachment.fileName}
                            </a>
                          ) : (
                            <p className="py-1 font-300 truncate min-w-0">
                              {expense.attachment.fileName}
                            </p>
                          )}
                        </>
                      ) : (
                        <p className="text-gray-400 py-1 ml-2">No file selected</p>
                      )}
                    </div>

                    <BaseButton
                      variant="outlined"
                      color="primary"
                      className="w-[80px] sm:w-[100px] lg:w-[120px] h-[32px] ml-4 shrink-0"
                      onClick={() => fileInputRefs.current[index]?.click()}
                    >
                      Choose File
                    </BaseButton>
                  </div>
                </div>
              </div>
            </div>
          ))}

          <div className="inline-block mt-4 cursor-pointer" onClick={handleAddExpense}>
            <div className="flex items-center">
              <i className="fa-solid fa-plus text-secondary"></i>
              <p className="text-secondary text-sm ml-1">Add Expense</p>
            </div>
          </div>
        </BaseCard>
      </div>

      <div
        className="d-flex content-center bg-white py-6"
        style={{ boxShadow: "0px 0px 14px 0px rgba(0, 0, 0, 0.07)" }}
      >
        <BaseButton
          className="px-[28px] py-[5px] bg-light text-primary hover:!text-primary"
          variant="outlined"
          color="primary"
          style={{ marginRight: "16px" }}
          onClick={() => close(false)}
        >
          Cancel
        </BaseButton>
        <BaseButton
          isLoading={isSaving}
          loaderSize={24}
          loaderColor="primary"
          className="px-[12px] py-[5px] min-w-32"
          color="primary"
          onClick={handleSubmit}
          disabled={!hasChanges || isSaving}
        >
          Save Changes
        </BaseButton>
      </div>
    </BaseModal>
  );
};

export default EditCompanyExpensesModal;
